import React from 'react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';

export const MARKETING_WEB_COLUMNS = [
  { id: 'name', label: 'Page', sortable: true, sortType: 'text', align: 'left', first: true, alwaysVisible: true },
  { id: 'type', label: 'Type', sortable: true, sortType: 'text', align: 'left' },
  { id: 'impressions', label: 'Impressions', sortable: true, sortType: 'number', align: 'right', alwaysVisible: true },
  { id: 'unique_visitors', label: 'Unique Visitors', sortable: true, sortType: 'number', align: 'right' },
];

export const WEB_SORT_TYPES = {
  text: { asc: 'A \u2192 Z', desc: 'Z \u2192 A' },
  number: { asc: 'Low \u2192 High', desc: 'High \u2192 Low' },
  date: { asc: 'Oldest first', desc: 'Newest first' },
};

const TYPE_COLORS = [
  '#1982C4', '#FAB714', '#8AC926', '#6A4C93', '#2CA8A0',
  '#F47C2C', '#6B7280', '#E86A92', '#1982C4', '#FAB714',
];

const TYPE_BADGE_COLORS = {
  Event: { background: '#eff8ff', color: '#175cd3', border: '1px solid #b2ddff' },
  Announcement: { background: '#f9f5ff', color: '#6941c6', border: '1px solid #d6bbfb' },
};

export const WEB_CONFIG = {
  sidebarKey: 'marketing_web',
  pageTitle: 'Web',
  pageSubtitle: 'Overview of your web impressions and page views.',
  defaultPreset: 'year_to_date',
  filterTitle: 'Filter Web',
  filterSubtitle: 'Apply filters to narrow web data.',
  apiEndpoint: (teamId) => `/teams/${teamId}/reporting/marketing-web/data.json`,

  columns: MARKETING_WEB_COLUMNS,
  sortTypes: WEB_SORT_TYPES,
  defaultSort: { field: 'impressions', direction: 'desc' },
  localStorageKey: (teamId) => `reportingMarketingWebColumns_${teamId}`,
  nameColumn: 'name',

  kpiColumns: 2,
  buildKpiCards: (kpis, prior, compactNumber) => [
    {
      top: { label: 'Total Impressions', value: compactNumber(kpis.total_impressions || 0), current: kpis.total_impressions, prior: prior.total_impressions, tooltip: 'Total page views in the period' },
      bottom: { label: 'Unique Visitors', value: compactNumber(kpis.unique_visitors || 0), current: kpis.unique_visitors, prior: prior.unique_visitors, tooltip: 'Distinct visitors based on session or IP' },
    },
    {
      top: { label: 'Pages Viewed', value: compactNumber(kpis.pages_viewed || 0), current: kpis.pages_viewed, prior: prior.pages_viewed, tooltip: 'Distinct pages that received impressions' },
      bottom: { label: 'Avg Views / Visitor', value: `${kpis.avg_per_visitor || 0}`, current: kpis.avg_per_visitor, prior: prior.avg_per_visitor, tooltip: 'Average impressions per unique visitor' },
    },
  ],

  engagementTitle: 'Impressions Over Time',
  engagementSubtitle: 'Page views and unique visitors',
  engagementSeries: [
    { dataKey: 'impressions', label: 'Impressions', color: '#1982C4' },
    { dataKey: 'visitors', label: 'Visitors', color: '#6A4C93' },
  ],
  engagementYFormatter: null,

  buildFunnelData: null,
  renderSecondChart: ({ data, loading, compactNumber, isMobile }) => {
    const byType = (data && data.by_type) || [];
    return (
      <div style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 8, padding: '20px 24px' }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: '#101828' }}>Impressions by Type</span>
        <p style={{ fontSize: 13, color: '#667085', marginTop: 4, marginBottom: 16 }}>Breakdown by content type</p>
        {byType.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={byType} margin={{ top: 5, right: 10, left: 0, bottom: 5 }} layout="vertical">
              <CartesianGrid horizontal={false} stroke="#E5E7EB" />
              <XAxis type="number" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} tickFormatter={(v) => compactNumber(v)} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 13, fill: '#9CA3AF', fontWeight: 500 }} axisLine={false} tickLine={false} width={110} />
              <Tooltip
                cursor={false}
                content={({ active, payload }) => {
                  if (!active || !payload || !payload.length) return null;
                  return (
                    <div style={{ background: '#000', borderRadius: 8, padding: 12, minWidth: 140 }}>
                      <p style={{ color: '#fff', fontSize: 12, margin: '0 0 8px 0' }}>{payload[0].payload.name}</p>
                      <div className="d-flex align-items-center" style={{ gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: payload[0].color || '#1982C4', flexShrink: 0 }} />
                        <span style={{ color: '#a1a1aa', fontSize: 12 }}>Impressions:</span>
                        <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{payload[0].value.toLocaleString()}</span>
                      </div>
                    </div>
                  );
                }}
              />
              <defs>
                {byType.map((_, idx) => (
                  <linearGradient key={`webTypeGrad${idx}`} id={`webTypeGradient${idx}`} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor={TYPE_COLORS[idx % TYPE_COLORS.length]} />
                    <stop offset="100%" stopColor={TYPE_COLORS[idx % TYPE_COLORS.length]} stopOpacity={0.5} />
                  </linearGradient>
                ))}
              </defs>
              <Bar dataKey="value" radius={[0, 4, 4, 0]} maxBarSize={48}>
                {byType.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={`url(#webTypeGradient${index})`} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div style={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#667085', fontSize: 14 }}>
            No impression data for the selected period.
          </div>
        )}
      </div>
    );
  },

  getRows: (data) => (data && data.top_pages) || [],
  getTotals: () => ({}),

  renderExtraCharts: ({ data, isMobile }) => {
    const topReferrers = (data && data.top_referrers) || [];
    if (topReferrers.length === 0) return null;
    return (
      <div style={{ background: '#fff', border: '1px solid #e4e7ec', borderRadius: 8, padding: '20px 24px', marginBottom: 24 }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: '#101828' }}>Top Referrers</span>
        <p style={{ fontSize: 13, color: '#667085', marginTop: 4, marginBottom: 16 }}>Where your traffic comes from</p>
        <ResponsiveContainer width="100%" height={Math.max(180, topReferrers.length * 36 + 40)}>
          <BarChart
            data={topReferrers.map((r) => ({ name: r.domain && r.domain.length > 40 ? r.domain.substring(0, 40) + '...' : r.domain, count: r.count, fullDomain: r.domain, referrer: r.referrer }))}
            layout="vertical"
            margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
          >
            <CartesianGrid horizontal={false} stroke="#E5E7EB" />
            <XAxis type="number" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} />
            <YAxis type="category" dataKey="name" tick={{ fontSize: 12, fill: '#9CA3AF' }} axisLine={false} tickLine={false} width={200} />
            <Tooltip
              cursor={false}
              content={({ active, payload }) => {
                if (!active || !payload || !payload.length) return null;
                return (
                  <div style={{ background: '#000', borderRadius: 8, padding: 12, minWidth: 140 }}>
                    <p style={{ color: '#fff', fontSize: 12, margin: '0 0 8px 0', wordBreak: 'break-all' }}>{payload[0].payload.fullDomain}</p>
                    <div className="d-flex align-items-center" style={{ gap: 6 }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#2CA8A0', flexShrink: 0 }} />
                      <span style={{ color: '#a1a1aa', fontSize: 12 }}>Visits:</span>
                      <span style={{ color: '#fff', fontSize: 12, fontWeight: 600 }}>{payload[0].value.toLocaleString()}</span>
                    </div>
                  </div>
                );
              }}
            />
            <defs>
              <linearGradient id="referrerGradient" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="#2CA8A0" />
                <stop offset="100%" stopColor="#8ED8D0" />
              </linearGradient>
            </defs>
            <Bar dataKey="count" fill="url(#referrerGradient)" radius={[0, 4, 4, 0]} maxBarSize={20} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  },

  topPerformersTitle: null,

  tableTitle: 'Top Pages',
  searchPlaceholder: 'Search pages...',
  emptyMessage: 'No impression data for the selected period.',
  emptySearchMessage: 'No pages match your search.',
  paginationLabel: 'pages',
  csvFilename: 'marketing_web_pages.csv',

  searchFilter: (r, q) =>
    (r.name || '').toLowerCase().includes(q) ||
    (r.type || '').toLowerCase().includes(q) ||
    String(r.id).includes(q),

  renderCellValue: (colId, row) => {
    switch (colId) {
      case 'name': return <div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{row.name || '\u2014'}</div>;
      case 'type': {
        const style = TYPE_BADGE_COLORS[row.type] || { background: '#f2f4f7', color: '#344054', border: '1px solid #e4e7ec' };
        return <span className="badge" style={{ ...style, fontSize: 12, fontWeight: 500, padding: '2px 8px', borderRadius: 12 }}>{row.type}</span>;
      }
      case 'impressions': return (row.impressions || 0).toLocaleString();
      case 'unique_visitors': return (row.unique_visitors || 0).toLocaleString();
      default: return '\u2014';
    }
  },

  buildCsvValue: (colId, row) => {
    switch (colId) {
      case 'name': return row.name || '';
      case 'type': return row.type || '';
      case 'impressions': return row.impressions || 0;
      case 'unique_visitors': return row.unique_visitors || 0;
      default: return '';
    }
  },
};
